import {
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { OrbBoardBaseComponent } from '../org-board-base-component';
import { Position, SecurityGroupDetails, UserDetails } from '../services/org-board-builder.service';
import { OrgBoardService } from '../services/org-board-service';
import { ProfileColorService } from '../services/profile-color.service';
import { CommonModule, NgStyle } from '@angular/common';
import { MaterialModule } from '../../material.module';
import { FormsModule } from '@angular/forms';
import { NormalizePipe } from '../format-pipe';
import { LinkOrigin, linkOrigin } from '../linked-posts';
import { ECONode, Organization, OrgHat } from '@newgenus/common';
import { RightSideBarService } from '../../../services/right-sidebar.service';

/** Resolved hat display info for this position */
interface HatOnPosition {
    key: string;
    name: string;
    description: string;
    securityGroupNames: string[];
    userNames: string[];
    userCount: number;
}

/** Resolved user display info for this position */
interface UserOnPosition {
    key: string;
    name: string;
    photoThumbnail?: string;
    photoURL?: string;
    hatNames: string[];
    otherPositionNames: string[];
    email?: string;
}

/** Resolved SG display info */
interface SecurityGroupOnPosition {
    key: string;
    name: string;
    features: string[];
    featureDetails: Record<string, { permissions?: string[] }>;
    description?: string;
    /** Feature key → description lookup */
    featureDescriptions: Record<string, string>;
    /** Feature key → { permissionKey → description } lookup */
    permissionDescriptions: Record<string, Record<string, string>>;
}

/** Resolved policy display info */
interface PolicyOnPosition {
    key: string;
    name: string;
    description?: string;
    category?: string;
    status?: string;
    hatName: string;
}

/** HFA user on this position (inherited from above) */
interface HfaUserOnPosition {
    key: string;
    name: string;
    photoThumbnail?: string;
    photoURL?: string;
    originPositionName: string;
    originPartKey: string;
    originPartType: string;
}

/** Colour preset from siblings/parent */
interface ColourPreset {
    label: string;
    colour: { background: string; color: string; linkColor: string };
}

@Component({
    selector: 'shared-sidebar-position',
    imports: [CommonModule, MaterialModule, NgStyle, FormsModule, NormalizePipe],
    template: `
        <!-- ═══ Position Header Card ═══ -->
        <mat-card class="position-header-card my-2">
            <mat-card-header>
                <div
                    mat-card-avatar
                    class="position-avatar"
                    [ngStyle]="{ 'background-color': nodeColour.background || '#757575' }"
                >
                    <mat-icon [ngStyle]="{ color: nodeColour.color || '#fff' }">business</mat-icon>
                </div>
                <mat-card-title>{{ node?.data?.name || 'Org Part' }}</mat-card-title>
                <mat-card-subtitle>
                    {{ $any(node?.data)?.partType | titlecase }} · Depth {{ node?.data?.depth }}
                    <span *ngIf="parentName"> · Parent: {{ parentName }}</span>
                </mat-card-subtitle>
            </mat-card-header>

            <mat-card-content class="pt-2">
                <div *ngIf="$any(node?.data)?.subTitle" class="position-subtitle">
                    {{ $any(node?.data)?.subTitle }}
                </div>
            </mat-card-content>

            <mat-card-actions align="end" class="button-margin">
                <button mat-stroked-button color="primary" (click)="dialogService.openNewHatDialog(node)">
                    <mat-icon>add</mat-icon> New Hat
                </button>
                <button
                    mat-stroked-button
                    color="primary"
                    (click)="dialogService.openNewHatDialog(node, undefined, undefined, 1)"
                >
                    <mat-icon>add_circle</mat-icon> Assign Hat
                </button>
            </mat-card-actions>
        </mat-card>

        <!-- ═══ Valuable Final Product (VFP) ═══ -->
        <mat-card class="my-2 vfp-card">
            <mat-card-header>
                <mat-icon mat-card-avatar class="section-icon vfp-icon" style="color: #ffd54f;">emoji_events</mat-icon>
                <mat-card-title>Valuable Final Product</mat-card-title>
            </mat-card-header>
            <mat-card-content>
                <div *ngIf="!isEditingVfp">
                    <p
                        *ngIf="$any(node?.data)?.vfp"
                        style="margin: 0; font-size: 14px; line-height: 1.6; font-weight: 700; white-space: pre-wrap; word-wrap: break-word; overflow-wrap: break-word;"
                    >
                        {{ $any(node?.data)?.vfp }}
                    </p>
                    <p *ngIf="!$any(node?.data)?.vfp" class="empty-msg">No VFP defined for this org part.</p>
                </div>
                <div *ngIf="isEditingVfp">
                    <textarea
                        #vfpInput
                        style="width: 100%; min-height: 100px; outline: none; background: rgba(0,0,0,0.15); border: 1px solid rgba(255,255,255,0.2); border-radius: 6px; color: inherit; font-size: 14px; padding: 12px; resize: vertical; font-family: inherit; line-height: 1.6; box-sizing: border-box;"
                        [value]="$any(node?.data)?.vfp || ''"
                        placeholder="Describe the Valuable Final Product of this org part..."
                    ></textarea>
                </div>
            </mat-card-content>
            <mat-card-actions *ngIf="!isEditingVfp && isEditing" align="end">
                <button mat-stroked-button style="font-size: 13px;" (click)="startEditingVfp()">
                    <mat-icon style="font-size: 18px; width: 18px; height: 18px; margin-right: 4px;">edit</mat-icon>
                    {{ $any(node?.data)?.vfp ? 'Edit VFP' : 'Add VFP' }}
                </button>
            </mat-card-actions>
            <mat-card-actions *ngIf="isEditingVfp" align="end">
                <button mat-stroked-button (click)="cancelEditingVfp()">Cancel</button>
                <button mat-flat-button color="primary" (click)="saveVfpFromInput()">Save</button>
            </mat-card-actions>
        </mat-card>

        <!-- ═══ Org Settings (posts only, edit mode) ═══ -->
        <mat-card class="my-2" *ngIf="isPost && isEditing">
            <mat-card-header>
                <mat-icon mat-card-avatar class="section-icon" style="color:#8a5a00;">account_tree</mat-icon>
                <mat-card-title>Org Settings</mat-card-title>
            </mat-card-header>
            <mat-card-content>
                <div class="setting-row">
                    <div class="setting-text">
                        <div class="setting-label">Part of running the org</div>
                        <div class="setting-hint">
                            When on, this post's occupant is "held from above" onto positions below it (the way the CEO
                            is). Turn it OFF for a governance/board seat that oversees but doesn't run the org — they'll
                            only show on their own post.
                        </div>
                    </div>
                    <mat-slide-toggle
                        color="primary"
                        [checked]="holdsFromAbove"
                        (change)="onHoldsFromAboveChange($event.checked)"
                    ></mat-slide-toggle>
                </div>
                <!-- Linked post: it represents another post and comes from there — read-only here;
                     links are set only by the migration (Paul, 2026-10-21). -->
                <div class="setting-row linked-post-row" *ngIf="origin as o">
                    <div class="setting-text">
                        <div class="setting-label linked-post-label">
                            <mat-icon class="linked-post-icon">link</mat-icon>
                            Represents {{ o.name }}
                        </div>
                        <div class="setting-hint linked-post-path">{{ o.path.join(' › ') }}</div>
                        <div class="setting-hint">Shows whoever holds {{ o.name }}. Change or staff it there.</div>
                    </div>
                    <button mat-stroked-button color="primary" class="linked-post-go" (click)="goToOrigin()">
                        Go to origin
                    </button>
                </div>
            </mat-card-content>
        </mat-card>

        <!-- ═══ Staff on this Org Part ═══ -->
        <mat-card class="my-2">
            <mat-card-header>
                <mat-icon mat-card-avatar class="section-icon">people</mat-icon>
                <mat-card-title>Staff ({{ users.length }})</mat-card-title>
            </mat-card-header>
            <mat-card-content>
                <div *ngIf="users.length === 0 && hfaUsers.length === 0" class="empty-msg">
                    No staff on this org part
                </div>
                <div *ngFor="let user of users" class="user-row" (click)="openUserDetails(user.key)">
                    <div class="user-avatar-sm">
                        <img [src]="getUserAvatarUrl(user)" [alt]="user.name" />
                    </div>
                    <div class="user-info">
                        <div class="user-name">{{ user.name | titlecase }}</div>
                        <div class="user-meta" *ngIf="user.hatNames.length">Hats: {{ user.hatNames.join(', ') }}</div>
                        <div class="user-meta" *ngIf="user.otherPositionNames.length">
                            Also on: {{ user.otherPositionNames.join(', ') }}
                        </div>
                    </div>
                    <mat-icon class="user-chevron">chevron_right</mat-icon>
                </div>

                <!-- HFA Users -->
                <div *ngIf="hfaUsers.length > 0" class="hfa-section">
                    <div class="hfa-label">
                        <mat-icon class="hfa-label-icon">north</mat-icon>
                        Held From Above
                    </div>
                    <div *ngFor="let hfa of hfaUsers" class="user-row hfa-user-row" (click)="openUserDetails(hfa.key)">
                        <div class="user-avatar-sm">
                            <img [src]="getUserAvatarUrl(hfa)" [alt]="hfa.name" />
                        </div>
                        <div class="user-info">
                            <div class="user-name">{{ hfa.name | titlecase }}</div>
                            <div class="user-meta hfa-origin">
                                From: <strong>{{ hfa.originPositionName }}</strong>
                                <span *ngIf="hfa.originPartType" class="hfa-part-type">({{ hfa.originPartType }})</span>
                            </div>
                        </div>
                        <mat-icon class="user-chevron">chevron_right</mat-icon>
                    </div>
                    <div class="hfa-nav-hint">
                        <button
                            *ngFor="let hfa of hfaUsers"
                            mat-stroked-button
                            class="hfa-nav-btn"
                            (click)="navigateToPosition(hfa.originPartKey)"
                            [matTooltip]="'Go to ' + hfa.originPositionName"
                        >
                            <mat-icon>open_in_new</mat-icon> {{ hfa.originPositionName }}
                        </button>
                    </div>
                </div>
            </mat-card-content>
        </mat-card>

        <!-- ═══ Hats on this Org Part ═══ -->
        <mat-card class="my-2">
            <mat-card-header>
                <mat-icon mat-card-avatar class="section-icon hat-icon">school</mat-icon>
                <mat-card-title>Hats ({{ hats.length }})</mat-card-title>
            </mat-card-header>
            <mat-card-content>
                <div *ngIf="hats.length === 0" class="empty-msg">No hats linked to this org part</div>
                <div *ngFor="let hat of hats" class="hat-row">
                    <div class="hat-header">
                        <mat-icon class="hat-row-icon">school</mat-icon>
                        <span class="hat-name">{{ hat.name }}</span>
                        <span class="hat-users" *ngIf="hat.userCount">
                            · {{ hat.userCount }} staff member{{ hat.userCount > 1 ? 's' : '' }}
                        </span>
                    </div>
                    <div class="hat-sg-list" *ngIf="hat.securityGroupNames.length">
                        <span class="sg-label">Security Groups:</span>
                        <span class="sg-chip" *ngFor="let sgName of hat.securityGroupNames" [matTooltip]="sgName">{{
                            sgName
                        }}</span>
                    </div>
                </div>
            </mat-card-content>
        </mat-card>

        <!-- ═══ Security Groups & Permissions ═══ -->
        <mat-card class="my-2">
            <mat-card-header>
                <mat-icon mat-card-avatar class="section-icon sg-icon-header">lock</mat-icon>
                <mat-card-title>Security Groups ({{ securityGroups.length }})</mat-card-title>
            </mat-card-header>
            <mat-card-content>
                <div *ngIf="securityGroups.length === 0" class="empty-msg">No security groups on this org part</div>
                <div *ngFor="let sg of securityGroups" class="sg-section">
                    <div class="sg-name-row" [matTooltip]="sg.description || ''">
                        <mat-icon class="sg-row-icon">lock</mat-icon>
                        <strong>{{ sg.name | normalize }}</strong>
                    </div>
                    <div *ngIf="sg.description" class="sg-description">{{ sg.description }}</div>
                    <div *ngFor="let feature of sg.features" class="feature-block">
                        <div class="feature-name" [matTooltip]="sg.featureDescriptions[feature] || ''">
                            <mat-icon class="feature-icon">star</mat-icon>
                            {{ feature | normalize }}
                        </div>
                        <div class="permission-list" *ngIf="sg.featureDetails[feature]?.permissions?.length">
                            <span
                                *ngFor="let perm of sg.featureDetails[feature].permissions"
                                class="perm-chip"
                                [matTooltip]="sg.permissionDescriptions[feature]?.[perm] || ''"
                            >
                                {{ perm | normalize }}
                            </span>
                        </div>
                    </div>
                </div>
            </mat-card-content>
        </mat-card>

        <!-- ═══ Colours ═══ -->
        <mat-card class="my-2">
            <mat-card-header>
                <mat-icon mat-card-avatar class="section-icon policy-icon">policy</mat-icon>
                <mat-card-title>Policies ({{ policies.length }})</mat-card-title>
            </mat-card-header>
            <mat-card-content>
                <div *ngIf="policies.length === 0" class="empty-msg">No policies on this org part</div>
                <div *ngFor="let policy of policies" class="policy-row">
                    <div class="policy-name-row" [matTooltip]="policy.name">
                        <mat-icon class="policy-row-icon">policy</mat-icon>
                        <strong>{{ policy.name | normalize }}</strong>
                    </div>
                    <div *ngIf="policy.description" class="policy-description">{{ policy.description }}</div>
                    <div class="policy-meta">
                        <span
                            *ngIf="policy.category"
                            class="policy-chip category-chip"
                            [matTooltip]="'Category: ' + policy.category"
                            >{{ policy.category }}</span
                        >
                        <span
                            *ngIf="policy.status"
                            class="policy-chip status-chip"
                            [matTooltip]="'Status: ' + policy.status"
                            >{{ policy.status }}</span
                        >
                        <span class="policy-hat-label" [matTooltip]="'Via hat: ' + policy.hatName"
                            >via {{ policy.hatName }}</span
                        >
                    </div>
                </div>
            </mat-card-content>
        </mat-card>

        <!-- ═══ Colours ═══ -->
        <mat-card class="my-2">
            <mat-card-header>
                <mat-icon mat-card-avatar class="section-icon colour-icon">palette</mat-icon>
                <mat-card-title>Org Part Colours</mat-card-title>
            </mat-card-header>
            <mat-card-content>
                <div class="colour-row">
                    <label>Text</label>
                    <div class="colour-swatch" [ngStyle]="{ background: nodeColour.color }"></div>
                    <input type="color" [(ngModel)]="nodeColour.color" (ngModelChange)="onColourChanged()" />
                </div>
                <div class="colour-row">
                    <label>Background</label>
                    <div class="colour-swatch" [ngStyle]="{ background: nodeColour.background }"></div>
                    <input type="color" [(ngModel)]="nodeColour.background" (ngModelChange)="onColourChanged()" />
                </div>
                <div class="colour-row">
                    <label>Link Color</label>
                    <div class="colour-swatch" [ngStyle]="{ background: nodeColour.linkColor }"></div>
                    <input type="color" [(ngModel)]="nodeColour.linkColor" (ngModelChange)="onColourChanged()" />
                </div>

                <!-- Colour presets from parent/siblings -->
                <div *ngIf="colourPresets.length > 0" class="colour-presets">
                    <div class="presets-label">Use colour from:</div>
                    <div class="preset-chips">
                        <button
                            *ngFor="let preset of colourPresets"
                            mat-stroked-button
                            class="preset-chip"
                            [matTooltip]="preset.label"
                            (click)="applyPreset(preset)"
                        >
                            <span class="preset-swatch" [ngStyle]="{ background: preset.colour.background }"></span>
                            {{ preset.label }}
                        </button>
                    </div>
                </div>
            </mat-card-content>
        </mat-card>

        <!-- ═══ Dev Position Details ═══ -->
        <mat-card class="my-2" *ngIf="isDev">
            <mat-card-header>
                <mat-icon mat-card-avatar class="section-icon">code</mat-icon>
                <mat-card-title>Dev Details</mat-card-title>
            </mat-card-header>
            <mat-card-content class="dev-details">
                <div class="dev-row">
                    <span class="dev-label">PartKey:</span
                    ><span class="dev-value">{{ node?.data?.partKey || '-' }}</span>
                </div>
                <div class="dev-row">
                    <span class="dev-label">Key:</span><span class="dev-value">{{ node?.data?.key || '-' }}</span>
                </div>
                <div class="dev-row">
                    <span class="dev-label">ID:</span><span class="dev-value">{{ node?.data?.id }}</span>
                </div>
                <div class="dev-row">
                    <span class="dev-label">Depth:</span><span class="dev-value">{{ node?.data?.depth }}</span>
                </div>
                <div class="dev-row">
                    <span class="dev-label">Parent:</span><span class="dev-value">{{ node?.data?.parent || '-' }}</span>
                </div>
                <div class="dev-row">
                    <span class="dev-label">Path:</span><span class="dev-value">{{ node?.data?.path || '-' }}</span>
                </div>
                <div class="dev-row">
                    <span class="dev-label">Type:</span
                    ><span class="dev-value">{{ $any(node?.data)?.partType || node?.data?.type || '-' }}</span>
                </div>
            </mat-card-content>
        </mat-card>
    `,
    styles: [
        `
            :host {
                display: block;
                width: 100%;
            }

            .vfp-card .mat-mdc-card-content:last-child {
                padding-bottom: 16px;
            }
            .vfp-card .mat-mdc-card-content {
                transition: none !important;
            }

            .sidebar-close-row {
                display: flex;
                justify-content: flex-end;
                padding: 4px 4px 0;
            }

            // --- Position Header ---
            .position-header-card {
                .position-avatar {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 50%;
                    width: 40px;
                    height: 40px;

                    mat-icon {
                        font-size: 22px;
                        width: 22px;
                        height: 22px;
                    }
                }

                .position-subtitle {
                    font-size: 13px;
                    color: rgba(255, 255, 255, 0.6);
                    margin-bottom: 8px;
                }
            }

            // --- Users Section ---
            .user-row {
                display: flex;
                align-items: center;
                gap: 10px;
                padding: 8px 4px;
                border-bottom: 1px solid rgba(255, 255, 255, 0.06);
                cursor: pointer;
                border-radius: 4px;
                transition: background 0.15s;

                &:hover {
                    background: rgba(255, 255, 255, 0.06);
                }
                &:last-child {
                    border-bottom: none;
                }
            }

            .user-avatar-sm {
                width: 36px;
                height: 36px;
                border-radius: 50%;
                overflow: hidden;
                flex-shrink: 0;

                img {
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;
                    object-fit: cover;
                }
            }

            .user-info {
                flex: 1;
                min-width: 0;
            }

            .user-name {
                font-weight: 500;
                font-size: 14px;
            }

            .user-meta {
                font-size: 12px;
                color: rgba(255, 255, 255, 0.5);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .user-chevron {
                font-size: 18px;
                width: 18px;
                height: 18px;
                color: rgba(255, 255, 255, 0.3);
            }

            // --- Hats Section ---
            .hat-row {
                padding: 8px 4px;
                border-bottom: 1px solid rgba(255, 255, 255, 0.06);
                &:last-child {
                    border-bottom: none;
                }
            }

            .hat-header {
                display: flex;
                align-items: center;
                gap: 6px;
            }

            .hat-row-icon {
                font-size: 18px;
                width: 18px;
                height: 18px;
                color: var(--entity-color-hats, #ff9800);
            }

            .hat-name {
                font-weight: 500;
                font-size: 14px;
            }

            .hat-users {
                font-size: 12px;
                color: rgba(255, 255, 255, 0.5);
            }

            .hat-sg-list {
                margin-top: 4px;
                padding-left: 24px;
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 4px;

                .sg-label {
                    font-size: 11px;
                    color: rgba(255, 255, 255, 0.45);
                    margin-right: 2px;
                }

                .sg-chip {
                    display: inline-block;
                    font-size: 11px;
                    padding: 1px 8px;
                    border-radius: 10px;
                    background: color-mix(in srgb, var(--entity-color-security-groups, #4caf50) 15%, transparent);
                    border: 1px solid color-mix(in srgb, var(--entity-color-security-groups, #4caf50) 30%, transparent);
                    color: var(--entity-color-security-groups, #a5d6a7);
                }
            }

            // --- SG / Features / Permissions ---
            .sg-section {
                padding: 8px 0;
                border-bottom: 1px solid rgba(255, 255, 255, 0.06);
                &:last-child {
                    border-bottom: none;
                }
            }

            .sg-name-row {
                display: flex;
                align-items: center;
                gap: 6px;
                margin-bottom: 4px;
            }

            .sg-row-icon {
                font-size: 16px;
                width: 16px;
                height: 16px;
                color: var(--entity-color-security-groups, #66bb6a);
            }

            .sg-description {
                font-size: 12px;
                color: rgba(255, 255, 255, 0.5);
                margin: 2px 0 6px 22px;
            }

            .feature-block {
                margin: 4px 0 4px 22px;
            }

            .feature-name {
                display: flex;
                align-items: center;
                gap: 4px;
                font-size: 13px;
                font-weight: 500;
            }

            .feature-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
                color: var(--entity-color-features, #ffd54f);
            }

            .permission-list {
                display: flex;
                flex-wrap: wrap;
                gap: 4px;
                padding: 3px 0 3px 18px;
            }

            .perm-chip {
                display: inline-block;
                font-size: 11px;
                padding: 1px 7px;
                border-radius: 10px;
                background: rgba(255, 255, 255, 0.06);
                border: 1px solid rgba(255, 255, 255, 0.1);
                color: rgba(255, 255, 255, 0.65);
            }

            // --- Colours ---
            .colour-row {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 6px 0;

                label {
                    width: 90px;
                    font-size: 13px;
                    color: rgba(255, 255, 255, 0.7);
                }

                .colour-swatch {
                    width: 22px;
                    height: 22px;
                    border-radius: 4px;
                    border: 1px solid rgba(255, 255, 255, 0.2);
                }

                input[type='color'] {
                    width: 32px;
                    height: 28px;
                    padding: 0;
                    border: none;
                    background: transparent;
                    cursor: pointer;
                }
            }

            .colour-presets {
                margin-top: 8px;
                padding-top: 8px;
                border-top: 1px solid rgba(255, 255, 255, 0.06);

                .presets-label {
                    font-size: 12px;
                    color: rgba(255, 255, 255, 0.5);
                    margin-bottom: 6px;
                }
            }

            .preset-chips {
                display: flex;
                flex-wrap: wrap;
                gap: 6px;
            }

            .preset-chip {
                font-size: 12px !important;
                padding: 2px 10px !important;
                height: 28px !important;
                line-height: 28px !important;

                .preset-swatch {
                    display: inline-block;
                    width: 12px;
                    height: 12px;
                    border-radius: 2px;
                    margin-right: 4px;
                    vertical-align: middle;
                    border: 1px solid rgba(255, 255, 255, 0.2);
                }
            }

            // --- Dev Details ---
            .dev-details {
                .dev-row {
                    display: flex;
                    padding: 3px 0;
                    font-size: 12px;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.04);
                }
                .dev-label {
                    width: 80px;
                    color: rgba(255, 255, 255, 0.5);
                    flex-shrink: 0;
                }
                .dev-value {
                    word-break: break-all;
                    color: rgba(255, 255, 255, 0.8);
                }
            }

            // --- Section & Empty ---
            .section-icon {
                background: rgba(255, 255, 255, 0.08);
                border-radius: 50%;
                padding: 6px;
                width: 36px;
                height: 36px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 20px;
            }

            .hat-icon {
                color: var(--entity-color-hats, #ff9800);
            }
            .sg-icon-header {
                color: var(--entity-color-security-groups, #66bb6a);
            }
            .policy-icon {
                color: var(--entity-color-policies, #42a5f5);
            }
            .colour-icon {
                color: #ce93d8;
            }

            // --- Policies Section ---
            .policy-row {
                padding: 8px 0;
                border-bottom: 1px solid rgba(255, 255, 255, 0.06);
                &:last-child {
                    border-bottom: none;
                }
            }

            .policy-name-row {
                display: flex;
                align-items: center;
                gap: 6px;
                margin-bottom: 4px;
            }

            .policy-row-icon {
                font-size: 16px;
                width: 16px;
                height: 16px;
                color: var(--entity-color-policies, #42a5f5);
            }

            .policy-description {
                font-size: 12px;
                color: rgba(255, 255, 255, 0.5);
                margin: 2px 0 6px 22px;
            }

            .policy-meta {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 6px;
                padding-left: 22px;
            }

            .policy-chip {
                display: inline-block;
                font-size: 11px;
                padding: 1px 8px;
                border-radius: 10px;
            }

            .category-chip {
                background: color-mix(in srgb, var(--entity-color-policies, #42a5f5) 15%, transparent);
                border: 1px solid color-mix(in srgb, var(--entity-color-policies, #42a5f5) 30%, transparent);
                color: var(--entity-color-policies, #90caf9);
            }

            .status-chip {
                background: rgba(255, 183, 77, 0.15);
                border: 1px solid rgba(255, 183, 77, 0.3);
                color: #ffcc80;
            }

            .policy-hat-label {
                font-size: 11px;
                color: rgba(255, 255, 255, 0.4);
                font-style: italic;
            }

            .empty-msg {
                font-size: 13px;
                color: rgba(255, 255, 255, 0.4);
                font-style: italic;
                padding: 8px 0;
            }

            .button-margin button {
                margin-left: 6px;
            }

            // --- Org Settings (toggle row) ---
            .setting-row {
                display: flex;
                align-items: flex-start;
                justify-content: space-between;
                gap: 12px;
            }
            .setting-text {
                flex: 1;
                min-width: 0;
            }
            .setting-label {
                font-size: 14px;
                font-weight: 500;
            }
            .setting-hint {
                font-size: 12px;
                color: rgba(255, 255, 255, 0.5);
                margin-top: 2px;
                line-height: 1.4;
            }
            :host-context(:not(.dark-theme)) .setting-hint {
                color: rgba(0, 0, 0, 0.54);
            }
            .linked-post-row {
                margin-top: 16px;
                align-items: flex-start;
                gap: 12px;
            }
            .linked-post-label {
                display: flex;
                align-items: center;
                gap: 6px;
            }
            .linked-post-icon {
                font-size: 18px;
                width: 18px;
                height: 18px;
                color: #7986cb;
            }
            .linked-post-go {
                flex: 0 0 auto;
            }

            // --- HFA Section ---
            .hfa-section {
                margin-top: 8px;
                padding-top: 8px;
                border-top: 1px dashed rgba(255, 255, 255, 0.15);
            }

            .hfa-label {
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 12px;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                color: #ffb74d;
                margin-bottom: 8px;
            }

            .hfa-label-icon {
                font-size: 16px;
                width: 16px;
                height: 16px;
                color: #ffb74d;
            }

            .hfa-user-row {
                border-left: 3px solid #ffb74d;
                padding-left: 8px;
            }

            .hfa-origin strong {
                color: rgba(255, 255, 255, 0.8);
            }

            .hfa-part-type {
                opacity: 0.5;
                margin-left: 4px;
            }

            .hfa-nav-hint {
                margin-top: 8px;
                display: flex;
                flex-wrap: wrap;
                gap: 6px;
            }

            .hfa-nav-btn {
                font-size: 12px !important;
                padding: 2px 10px !important;
                height: 28px !important;
                line-height: 28px !important;
                color: #ffb74d !important;
                border-color: rgba(255, 183, 77, 0.4) !important;

                mat-icon {
                    font-size: 14px;
                    width: 14px;
                    height: 14px;
                    margin-right: 4px;
                }
            }

            // ═══ Light theme overrides ═══
            :host-context(:not(.dark-theme)) {
                .position-header-card .position-subtitle {
                    color: rgba(0, 0, 0, 0.54);
                }

                .user-row {
                    border-color: rgba(0, 0, 0, 0.08);
                    &:hover {
                        background: rgba(0, 0, 0, 0.04);
                    }
                }

                .user-meta {
                    color: rgba(0, 0, 0, 0.54);
                }
                .user-chevron {
                    color: rgba(0, 0, 0, 0.38);
                }

                .hat-row {
                    border-color: rgba(0, 0, 0, 0.08);
                }
                .hat-users {
                    color: rgba(0, 0, 0, 0.54);
                }

                .hat-sg-list {
                    .sg-label {
                        color: rgba(0, 0, 0, 0.5);
                    }
                    .sg-chip {
                        background: color-mix(in srgb, var(--entity-color-security-groups, #4caf50) 10%, transparent);
                        border-color: color-mix(in srgb, var(--entity-color-security-groups, #4caf50) 35%, transparent);
                        color: var(--entity-color-security-groups, #2e7d32);
                    }
                }

                .sg-section {
                    border-color: rgba(0, 0, 0, 0.08);
                }
                .sg-description {
                    color: rgba(0, 0, 0, 0.54);
                }

                .perm-chip {
                    background: rgba(0, 0, 0, 0.05);
                    border-color: rgba(0, 0, 0, 0.12);
                    color: rgba(0, 0, 0, 0.65);
                }

                .section-icon {
                    background: rgba(0, 0, 0, 0.06);
                }

                .colour-row {
                    label {
                        color: rgba(0, 0, 0, 0.7);
                    }
                    .colour-swatch {
                        border-color: rgba(0, 0, 0, 0.2);
                    }
                }

                .colour-presets {
                    border-color: rgba(0, 0, 0, 0.08);
                    .presets-label {
                        color: rgba(0, 0, 0, 0.54);
                    }
                }

                .preset-chip .preset-swatch {
                    border-color: rgba(0, 0, 0, 0.2);
                }

                .policy-row {
                    border-color: rgba(0, 0, 0, 0.08);
                }
                .policy-description {
                    color: rgba(0, 0, 0, 0.54);
                }

                .category-chip {
                    background: color-mix(in srgb, var(--entity-color-policies, #1976d2) 8%, transparent);
                    border-color: color-mix(in srgb, var(--entity-color-policies, #1976d2) 30%, transparent);
                    color: var(--entity-color-policies, #1565c0);
                }

                .status-chip {
                    background: rgba(255, 152, 0, 0.08);
                    border-color: rgba(255, 152, 0, 0.3);
                    color: #e65100;
                }

                .policy-hat-label {
                    color: rgba(0, 0, 0, 0.45);
                }

                .empty-msg {
                    color: rgba(0, 0, 0, 0.45);
                }

                .hfa-section {
                    border-color: rgba(0, 0, 0, 0.12);
                }
                .hfa-origin strong {
                    color: rgba(0, 0, 0, 0.87);
                }

                .hfa-nav-btn {
                    color: #e65100 !important;
                    border-color: rgba(230, 81, 0, 0.35) !important;
                }

                .collapsible-header:hover {
                    background: rgba(0, 0, 0, 0.04);
                }

                .dev-details {
                    .dev-row {
                        border-color: rgba(0, 0, 0, 0.06);
                    }
                    .dev-label {
                        color: rgba(0, 0, 0, 0.54);
                    }
                    .dev-value {
                        color: rgba(0, 0, 0, 0.87);
                    }
                }
            }
        `,
    ],
})
export class SidebarPositionComponent extends OrbBoardBaseComponent implements OnInit, OnChanges {
    @Input() public isDev = false;
    @Input() public isEditing = false;
    @Input() public node!: ECONode<Position>;

    /** Emitted when user clicks "Go to" an HFA origin position. Payload is the origin partKey. */
    @Output() public navigateToHfaOrigin = new EventEmitter<string>();

    public obService = inject(OrgBoardService);
    private sidebarService = inject(RightSideBarService);
    private profileColorService = inject(ProfileColorService);

    public users: UserOnPosition[] = [];
    public hfaUsers: HfaUserOnPosition[] = [];
    public hats: HatOnPosition[] = [];
    public securityGroups: SecurityGroupOnPosition[] = [];
    public policies: PolicyOnPosition[] = [];
    public colourPresets: ColourPreset[] = [];
    public parentName = '';
    public isEditingVfp = false;

    public override ngOnInit(): void {
        this.resolveData();
    }

    /** This part is a Post (only Posts are HFA sources / can hold from above). */
    public get isPost(): boolean {
        return (this.node?.data as any)?.partType === 'post';
    }

    /** Toggle state: true = participates in running the org (an HFA source). */
    public get holdsFromAbove(): boolean {
        return !(this.node?.data as any)?.excludeFromHfa;
    }

    /**
     * Toggles whether this post is "held from above" onto positions below. OFF
     * sets `excludeFromHfa:true` (board/governance seat — no cascade). Rebuilds
     * the board immediately (so HFA recomputes) and persists via updatePart.
     */
    public async onHoldsFromAboveChange(checked: boolean): Promise<void> {
        const partKey = this.node?.data?.partKey || (this.node?.data as any)?.key;
        if (!partKey || !this.org) return;
        const excludeFromHfa = !checked;

        // Local state (node + org array) so the rebuild sees the new value.
        (this.node.data as any).excludeFromHfa = excludeFromHfa;
        const orgPart = this.org.orgParts?.find((p) => p.partKey === partKey);
        if (orgPart) (orgPart as any).excludeFromHfa = excludeFromHfa;

        // Immediate board rebuild → HFA cascade recomputes now.
        this.obService.partSettingChanged.next({ partKey, changes: { excludeFromHfa } });

        // Persist (parts subcollection → trigger syncs the org-doc cache).
        try {
            await this.obService.updatePart({ partKey, excludeFromHfa } as any, this.org, false);
        } catch (error) {
            console.error('[sidebar-position] Failed to save excludeFromHfa:', error);
        }
    }

    /** What this post represents when it is a linked post (null otherwise) — recomputed with the
     *  node/org so the template reads a stable reference. */
    public origin: LinkOrigin | null = null;

    /** "Go to origin": the board scrolls to the represented post and opens it. */
    public goToOrigin(): void {
        if (this.origin) this.obService.navigateToPart.next(this.origin.partKey);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['node'] || changes['org']) {
            const selfKey = this.node?.data?.partKey || (this.node?.data as any)?.key;
            this.origin = linkOrigin((this.org?.orgParts as any[]) || [], selfKey);
            this.resolveData();
        }
    }

    private resolveData(): void {
        if (!this.node?.data || !this.org) return;

        const position = this.node.data;
        const orgData = this.org;

        // Resolve parent name
        this.parentName = this.findParentName(position, orgData);

        // Resolve users on this position
        this.users = this.resolveUsers(position, orgData);

        // Resolve HFA users (only when no direct users)
        this.hfaUsers = this.resolveHfaUsers(position, orgData);

        // Resolve hats linked to this position
        this.hats = this.resolveHats(position, orgData);

        // Resolve security groups from hats (SGs come through hats, not directly on position)
        this.securityGroups = this.resolveSecurityGroups(position, orgData, this.hats);

        // Resolve policies from hats
        this.policies = this.resolvePolicies(position, orgData, this.hats);

        // Build colour presets from parent and siblings
        this.colourPresets = this.buildColourPresets();

        // Ensure profile colors are loaded for all users
        const allUserKeys = [...this.users.map((u) => u.key), ...this.hfaUsers.map((u) => u.key)].filter(Boolean);
        if (allUserKeys.length) {
            this.profileColorService.ensureLoadedMany(allUserKeys);
        }
    }

    private findParentName(position: Position, org: Organization): string {
        if (!position.parentPartKey || !org.orgParts) return '';
        const parts = org.orgParts;
        for (const part of parts) {
            if (part.partKey === position.parentPartKey) return part.name;
        }
        // Try looking in the tree
        if (this.node?.nodeParent?.data?.name) return (this.node.nodeParent.data as Position).name;
        return '';
    }

    private resolveUsers(position: Position, org: Organization): UserOnPosition[] {
        const userDetailsArray = position.userDetailsArray || [];
        const orgUsers = org.orgUsers;
        const currentPartKey = position.partKey;

        return userDetailsArray.map((ud) => {
            const orgUser = orgUsers?.[ud.key];
            const hatNames = this.getHatNamesForUser(ud.key, org);
            // Other positions this user is on (exclude current)
            const otherPositionNames = (ud.positions || [])
                .filter((p) => p.partKey !== currentPartKey)
                .map((p) => p.name);

            return {
                key: ud.key,
                name: ud.name || orgUser?.name || ud.key,
                photoThumbnail: ud.photoThumbnail || orgUser?.photoThumbnail,
                photoURL: ud.photoURL || orgUser?.photoURL,
                hatNames,
                otherPositionNames,
                email: orgUser?.email,
            };
        });
    }

    private getHatNamesForUser(userKey: string, org: Organization): string[] {
        const hatKeys = org.orgHats?.hatsKeys || [];
        const names: string[] = [];
        for (const hk of hatKeys) {
            const hat = org.orgHats?.[hk];
            if (!hat || hat.isSoftDeleted) continue;
            const assignedUsers = hat.assignedToArray || hat.userKeys || [];
            if (assignedUsers.includes(userKey)) {
                names.push(hat.name);
            }
        }
        return names;
    }

    private resolveHats(position: Position, org: Organization): HatOnPosition[] {
        // Match ONLY hats linked to THIS position's partKey (same logic as position details dialog)
        const positionKey = position.partKey || position.key || '';

        const hatKeys = org.orgHats?.hatsKeys || [];
        const result: HatOnPosition[] = [];

        for (const hk of hatKeys) {
            const hat = org.orgHats?.[hk] as OrgHat;
            if (!hat || hat.isSoftDeleted) continue;

            // Check if hat's positionArray includes this position's partKey
            const positionArray = hat.positionArray || [];
            const isLinked = positionArray.includes(positionKey);

            if (!isLinked) continue;

            // Resolve SG names
            const sgKeys = (hat.securityGroupsArray || hat.securityGroups || []).map((sg: string | { key: string }) =>
                typeof sg === 'string' ? sg : sg.key
            );
            const sgNames = sgKeys
                .filter((k: string) => org.orgSecurityGroups?.[k])
                .map((k: string) => org.orgSecurityGroups[k].name || k);

            // Resolve user names
            const userKeys = hat.assignedToArray || hat.userKeys || [];
            const userNames = userKeys.filter((k) => org.orgUsers?.[k]).map((k) => org.orgUsers[k].name || k);

            result.push({
                key: hat.key,
                name: hat.name,
                description: hat.description || '',
                securityGroupNames: sgNames,
                userNames,
                userCount: userKeys.length,
            });
        }

        return result;
    }

    private resolveSecurityGroups(
        position: Position,
        org: Organization,
        hats: HatOnPosition[]
    ): SecurityGroupOnPosition[] {
        const seen = new Set<string>();
        const result: SecurityGroupOnPosition[] = [];

        // Collect SG keys from all hats linked to this position
        for (const hat of hats) {
            const hatObj = org.orgHats?.[hat.key] as OrgHat;
            if (!hatObj) continue;

            const sgEntries = hatObj.securityGroupsArray || hatObj.securityGroups || [];
            for (const entry of sgEntries) {
                const sgKey = typeof entry === 'string' ? entry : (entry as { key: string }).key;
                if (!sgKey || seen.has(sgKey)) continue;
                seen.add(sgKey);

                const orgSg = org.orgSecurityGroups?.[sgKey];
                if (!orgSg) continue;

                const features = orgSg.features || [];
                const featureDescriptions: Record<string, string> = {};
                const permissionDescriptions: Record<string, Record<string, string>> = {};
                for (const fKey of features) {
                    const orgFeature = org.orgFeatures?.[fKey];
                    if (orgFeature?.description) featureDescriptions[fKey] = orgFeature.description;
                    if (orgFeature?.permissionDescriptions)
                        permissionDescriptions[fKey] = orgFeature.permissionDescriptions;
                }

                result.push({
                    key: sgKey,
                    name: orgSg.name || sgKey,
                    features,
                    featureDetails: orgSg.featureDetails || {},
                    description: (orgSg as typeof orgSg & { description?: string }).description,
                    featureDescriptions,
                    permissionDescriptions,
                });
            }
        }

        // Also include any SGs directly on position.securityGroupsArray (fallback)
        const sgArray = position.securityGroupsArray || [];
        for (const sg of sgArray) {
            if (!sg.key || seen.has(sg.key)) continue;
            seen.add(sg.key);
            const sgFeatures = sg.features || [];
            const sgFeatureDescriptions: Record<string, string> = {};
            const sgPermissionDescriptions: Record<string, Record<string, string>> = {};
            for (const fKey of sgFeatures) {
                const orgFeature = org.orgFeatures?.[fKey];
                if (orgFeature?.description) sgFeatureDescriptions[fKey] = orgFeature.description;
                if (orgFeature?.permissionDescriptions)
                    sgPermissionDescriptions[fKey] = orgFeature.permissionDescriptions;
            }

            result.push({
                key: sg.key,
                name: sg.name || '',
                features: sgFeatures,
                featureDetails: sg.featureDetails || {},
                description: (sg as SecurityGroupDetails & { description?: string }).description,
                featureDescriptions: sgFeatureDescriptions,
                permissionDescriptions: sgPermissionDescriptions,
            });
        }

        return result;
    }

    private resolvePolicies(position: Position, org: Organization, hats: HatOnPosition[]): PolicyOnPosition[] {
        const result: PolicyOnPosition[] = [];
        const orgPolicies = (org as unknown as Record<string, unknown>)['orgPolicies'] as
            | Record<string, unknown>
            | undefined;
        if (!orgPolicies) return result;

        const orgHats = org.orgHats || ({} as Record<string, unknown>);
        const positionHatKeys = hats.map((h) => h.key);
        const positionPartKey = position.partKey || position.key || '';

        // Iterate ALL org policies and check if they target this position
        const policyKeys = (orgPolicies['policiesKeys'] as string[]) || [];
        const seen = new Set<string>();

        for (const policyKey of policyKeys) {
            if (!policyKey || seen.has(policyKey)) continue;
            seen.add(policyKey);

            const policy = orgPolicies[policyKey] as Record<string, unknown> | undefined;
            if (!policy) continue;

            // Only include published policies
            if (policy['status'] !== 'published') continue;

            const targets = policy['targets'] as Record<string, unknown> | undefined;
            if (!targets) continue;

            let viaLabel = '';

            // 1. Check if policy targets entire organization
            if (targets['mode'] === 'all_organization') {
                viaLabel = 'Entire Organization';
            } else {
                // 2. Check if position's hats are in resolvedHatKeys
                const resolvedHatKeys: string[] = (targets['resolvedHatKeys'] as string[]) || [];
                const matchingHatKeys = positionHatKeys.filter((hk) => resolvedHatKeys.includes(hk));

                // 3. Check if position's partKey is directly in targets.parts[]
                const parts = (targets['parts'] as Array<Record<string, unknown>>) || [];
                const directPart = parts.find((p) => p['key'] === positionPartKey);

                // 4. Check if an ancestor is in targets.parts with includeChildren
                let ancestorPart: Record<string, unknown> | undefined;
                if (!directPart && parts.length > 0) {
                    let ancestor = this.node?.nodeParent;
                    while (ancestor && !ancestorPart) {
                        const aPos = ancestor.data as Position;
                        const aKey = aPos?.partKey || aPos?.key || '';
                        ancestorPart = parts.find((p) => p['key'] === aKey && p['includeChildren'] === true);
                        ancestor = ancestor.nodeParent;
                    }
                }

                if (matchingHatKeys.length === 0 && !directPart && !ancestorPart) continue;

                // Build "via" label
                const labels: string[] = [];
                if (matchingHatKeys.length > 0) {
                    const hatNames = matchingHatKeys.map((hk) => (orgHats[hk] as OrgHat)?.name || hk).filter(Boolean);
                    labels.push(...hatNames);
                }
                if (directPart) {
                    labels.push('Direct target');
                }
                if (ancestorPart) {
                    labels.push('via ' + ((ancestorPart['name'] as string) || 'parent'));
                }
                viaLabel = labels.join(', ');
            }

            result.push({
                key: policyKey,
                name: (policy['name'] as string) || policyKey,
                description: policy['description'] as string | undefined,
                category: policy['category'] as string | undefined,
                status: policy['status'] as string | undefined,
                hatName: viaLabel,
            });
        }

        return result;
    }

    private buildColourPresets(): ColourPreset[] {
        const presets: ColourPreset[] = [];

        // Parent colour
        if (this.node?.nodeParent?.data?.colour) {
            const parentColour = (this.node.nodeParent.data as Position).colour;
            presets.push({
                label: 'Parent: ' + ((this.node.nodeParent.data as Position).name || 'Parent'),
                colour: { ...parentColour },
            });
        }

        // Sibling colours (unique only)
        const parentNode = this.node?.nodeParent;
        if (parentNode?.nodeChildren) {
            const seen = new Set<string>();
            for (const sibling of parentNode.nodeChildren) {
                if (sibling.id === this.node.id) continue;
                if (!sibling.data?.colour) continue;
                const bg = sibling.data.colour.background;
                if (seen.has(bg)) continue;
                seen.add(bg);
                presets.push({
                    label: sibling.data.name || 'Sibling',
                    colour: { ...sibling.data.colour },
                });
            }
        }

        return presets;
    }

    // --- Colour helpers ---

    public get nodeColour(): { color: string; background: string; linkColor: string } {
        if (!this.node?.data) return { color: '#000000', background: '#FFFFFF', linkColor: '#000000' };
        if (!this.node.data.colour) {
            this.node.data.colour = { color: '#000000', background: '#FFFFFF', linkColor: '#000000' };
        }
        return this.node.data.colour;
    }

    public onColourChanged(): void {
        this.obService.onColourChange.next(this.node);
    }

    public applyPreset(preset: ColourPreset): void {
        if (!this.node?.data) return;
        this.node.data.colour = { ...preset.colour };
        this.obService.onColourChange.next(this.node);
    }

    // --- User helpers ---

    public getInitials(name: string): string {
        if (!name) return '?';
        const parts = name.trim().split(/\s+/);
        return parts.length >= 2
            ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
            : name.substring(0, 2).toUpperCase();
    }

    /** Returns the profile background color for a user, or the default grey */
    public getProfileColor(userKey: string): string {
        return this.profileColorService.getColor(userKey) || '#616161';
    }

    /**
     * Gets the avatar URL for a user.
     * Returns photoThumbnail if available, otherwise falls back to generated avatar with profile color.
     */
    public getUserAvatarUrl(user: UserOnPosition | HfaUserOnPosition): string {
        if (user.photoThumbnail) return user.photoThumbnail;
        return this.profileColorService.getAvatarUrl(user.key, user.name, 36);
    }

    private static readonly CONTAINER_PART_TYPES = new Set(['executive', 'division', 'department', 'section', 'unit']);

    private resolveHfaUsers(position: Position, org: Organization): HfaUserOnPosition[] {
        // Only Posts show HFA — containers are structural and don't display HFA
        const partType = (position as any).partType as string;
        if (SidebarPositionComponent.CONTAINER_PART_TYPES.has(partType)) return [];

        // Only show HFA when there are no direct users
        if (position.userDetailsArray && position.userDetailsArray.length > 0) return [];

        const hfaUidStr = position.heldFromAboveUid;
        if (!hfaUidStr) return [];

        const hfaUids = hfaUidStr
            .split(',')
            .map((u) => u.trim())
            .filter(Boolean);
        if (hfaUids.length === 0) return [];

        const orgUsers = org.orgUsers;
        const result: HfaUserOnPosition[] = [];

        // Walk up the ECONode tree to find the origin position of each HFA user
        for (const uid of hfaUids) {
            const orgUser = orgUsers?.[uid];
            const originInfo = this.findHfaOrigin(uid);

            result.push({
                key: uid,
                name: orgUser?.name || uid,
                photoThumbnail: orgUser?.photoThumbnail,
                photoURL: orgUser?.photoURL,
                originPositionName: originInfo?.name || 'Unknown',
                originPartKey: originInfo?.partKey || '',
                originPartType: originInfo?.partType || '',
            });
        }
        return result;
    }

    /** Walk up ECONode tree to find which ancestor Post holds this user (skip containers) */
    private findHfaOrigin(userUid: string): { name: string; partKey: string; partType: string } | null {
        let current = this.node?.nodeParent;
        while (current) {
            const pos = current.data as Position;
            const posPartType = (pos as any)?.partType as string;
            // Only Posts can be HFA sources — skip containers
            if (
                !SidebarPositionComponent.CONTAINER_PART_TYPES.has(posPartType) &&
                pos?.userDetailsArray?.some((u: UserDetails) => u.key === userUid)
            ) {
                return {
                    name: pos.name || 'Unknown',
                    partKey: pos.partKey || pos.key || '',
                    partType: posPartType || '',
                };
            }
            current = current.nodeParent;
        }
        return null;
    }

    /** Navigate to the origin position — emits event for the board to handle */
    public navigateToPosition(partKey: string): void {
        if (!partKey) return;
        this.navigateToHfaOrigin.emit(partKey);
    }

    public openUserDetails(userKey: string): void {
        const orgUser = this.org?.orgUsers?.[userKey];
        if (!orgUser) return;
        const userForDialog = { ...orgUser, key: userKey, name: orgUser.name || '' };
        this.dialogService.openOrgUserDetailsDialog(this.node, userForDialog);
    }

    public closeSidebar(): void {
        this.sidebarService.close$.next(true);
    }

    // ═══ VFP editing methods ═══

    @ViewChild('vfpInput') vfpInputRef!: ElementRef<HTMLTextAreaElement>;

    public startEditingVfp(): void {
        this.isEditingVfp = true;
        setTimeout(() => {
            const el = this.vfpInputRef?.nativeElement;
            if (el) {
                el.focus();
                el.selectionStart = el.selectionEnd = el.value.length;
            }
        });
    }

    public cancelEditingVfp(): void {
        this.isEditingVfp = false;
    }

    public saveVfpFromInput(): void {
        const value = this.vfpInputRef?.nativeElement?.value || '';
        this.saveVfp(value);
    }

    public async saveVfp(value: string): Promise<void> {
        this.isEditingVfp = false;
        const partKey = this.node?.data?.partKey || (this.node?.data as any)?.key;
        if (!partKey || !this.org) return;

        // Update local state
        (this.node.data as any).vfp = value;

        // Also update orgParts on the org object so it stays in sync
        const orgPart = this.org.orgParts?.find((p) => p.partKey === partKey);
        if (orgPart) {
            orgPart.vfp = value;
        }

        // Save to Firestore via subcollection
        try {
            await this.obService.updatePart({ partKey, vfp: value } as any, this.org, false);
            console.log('[sidebar-position] VFP saved successfully for part:', partKey);
        } catch (error) {
            console.error('[sidebar-position] Failed to save VFP:', error);
        }
    }
}
